import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { startOfDay } from 'date-fns';
import {
  ModalService,
  TwoButtonModalV2,
  TwoButtonModalV2Component,
  DocumentsModalV2Component,
  BackendResponse,
} from '@schenck/rotec-components';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, switchMap, tap, map } from 'rxjs/operators';
import { TaskBase } from 'src/api';
import { TaskBaseService } from 'src/services/task-base.service';

/**
 * Base class for task modals. Should be extended and instantiated
 * in the modal so all sub components have access to it.
 *
 * Provided like this:
  ```ts
  providers: [
    YourExtensionOfTheTaskModalService,
    { provide: TaskModalService, useExisting: YourExtensionOfTheTaskModalService },
  ]
  ```
 */
@Injectable()
export abstract class TaskModalService<Task extends TaskBase> {
  protected _task$ = new BehaviorSubject<Task>(null);
  protected id: string;

  task$ = this._task$.pipe(filter((task) => !!task));
  taskLoading$ = new BehaviorSubject(false);

  constructor(
    protected taskService: TaskBaseService<Task>,
    protected modalService: ModalService,
    protected editModalComponent: ComponentType<any> | null
  ) {}

  /**
   * Opens edit modal for current task.
   * Returns true if it needs to be closed.
   */
  openEditModal$() {
    if (!this.editModalComponent) {
      return;
    }

    return this.modalService
      .open<Task, (Task | null)[]>(this.editModalComponent, this._task$.value)
      .events$.pipe(
        map((result) => {
          // the wizards return an array of infos for create. for update we know it' always of length 1
          // if a response exists, it means the task is still valid so we reload it.
          if (result[0]) {
            this._task$.next(result[0]);
            return false;
          }
          return true;
        })
      );
  }

  /**
   * Sets id of the task the modal is about. Should only be set by the parent component once on startup.
   */
  setId(id: string) {
    this.id = id;
    this.loadTask();
  }

  /**
   * Opens documents attached to this task.
   */
  openDocumentsModal() {
    this.modalService.open(DocumentsModalV2Component, this._task$.value.documentIds);
  }

  /**
   * Opens "do you really want to delete this?"-modal.
   * Returns observable which emits a value if the task was deleted.
   */
  delete$() {
    return this.modalService
      .open(TwoButtonModalV2Component, <TwoButtonModalV2>{
        title: 'Delete',
        text: !!this._task$.value.scheduling
          ? 'Do you really want to delete this task series?'
          : 'Do you really want to delete this task?',
        cancelText: 'Cancel',
        confirmText: 'Ok',
        darkMode: true,
        warning: true,
      })
      .events$.pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() => this.taskService.delete$(this._task$.value.id))
      );
  }

  /**
   * Sets task to done.
   */
  done$(description: string) {
    return this.taskService.done$(this.id, description);
  }

  /**
   * Skips task.
   */
  skip$(description: string) {
    return this.taskService.skip$(this.id, description);
  }

  /**
   * Shifts task.
   */
  shift$(description: string, newDueDate: Date) {
    return this.taskService.shift$(this.id, description, startOfDay(newDueDate));
  }

  /**
   * Updates task's notes.
   */
  editNotes$(notes: string) {
    const updatedTask = { ...this._task$.value, notes };
    return this.taskService.update$(updatedTask).pipe(
      tap((resp) => {
        if (BackendResponse.isSuccess(resp)) {
          this._task$.next(updatedTask);
        } else {
          // emit same value to force visual reset of notes
          this._task$.next(this._task$.value);
        }
      })
    );
  }

  /**
   * Reactivates task.
   */
  undo$() {
    return this.taskService.undo$(this.id);
  }

  /**
   * Sets documents only relevant for this single task.
   */
  setTaskDocuments$(fileIds: string[]): Observable<void> {
    this.taskLoading$.next(true);
    return this.taskService.setDocuments$(this.id, fileIds).pipe(tap(() => this.loadTask()));
  }

  /**
   * (Re)loads task.
   */
  protected loadTask() {
    this.taskLoading$.next(true);
    this.taskService.get$(this.id).subscribe((task) => {
      this._task$.next(task);
      this.taskLoading$.next(false);
    });
  }
}
